import { ApolloQueryResult } from '@apollo/client';
import { capitalize } from 'lodash';
import moment from 'moment';
import React from 'react';
import { Link } from 'react-router-dom';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Form, { ErrorMessage, Field, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import SectionMessage from '@atlaskit/section-message';
import Textfield from '@atlaskit/textfield';
import { Promotion } from '@css-xp/graphtypes';
import { EntitlementDetails } from '@css-xp/graphtypes/src/generated-types-new';
import { updateEntitlementPlan, updateEntitlementPlanV2 } from '@atlassian/temp-proxies';
import { OMSMetadata, OrderItemType } from '@atlassian/cssxp-types-entitlement';
import { constructCustomOrderRequest } from '@atlassian/cssxp-utils-entitlement';
import { NEW_DESIGN_DATE_FORMAT, UpdateOrderRequest, CustomOrderEntitlement } from '@atlassian/cssxp-typescommon';
import { ENABLE_OMS_SERVICE_V2 } from '@atlassian/cssxp-utils-generic';
import { useApprovalDeskPermissions } from '@css-xp/apd-common';
import { TaskDetailAction, useCancelRequest, useTaskList } from '@css-xp/apd-task-manager';
import {
  CopyableText,
  EntitlementIcon,
  ErrorComponents,
  InfoPanel,
  NotificationTypes,
  SummarySectionV2,
  formattedV2,
  successNotificationBuilder,
  useFormSubmit,
  useNotification,
  useSidebar,
  useFeatureToggle,
} from '@css-xp/base';
import { withApollo } from '@css-xp/base/graphql-provider';
import './rebill-subscription-pane.scss';
import { analytics, AnalyticsTrackAction, AnalyticsTrackSubject } from '@atlassian/temp-analytics';

interface Props {
  entitlementId: string;
  entitlementNumber: EntitlementDetails['entitlementNumber'];
  entitlement: EntitlementDetails;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
}

interface OfferingSelectItem {
  label: string;
  value: {
    id: string;
  };
}

interface SelectItem {
  label: string;
  value: string;
}

interface FormData {
  offeringSelectedItem?: OfferingSelectItem;
  supportTicketNumber?: string | null;
  handNote?: string | null;
  pricingPlan?: string;
  promotionId?: { promotion: Promotion };
  billingFrequency?: SelectItem;
  reasonCode?: string;
}

const getNotificationContent = () => {
  return (
    <div>
      <p>
        The subscription has been changed. A new invoice will be available in 24 hours. You can view the changes in{' '}
        <strong>Order history</strong>
      </p>
      <Link to={`#order-history`}>View order history</Link>
    </div>
  );
};

export const RebillSubscription = ({ entitlementId, entitlementNumber, entitlement, refetch }: Props) => {
  const { closeSidebar } = useSidebar();

  const productName = entitlement.productName;
  const productId = entitlement.productId;
  const isCloud = entitlement.platform?.trim().toUpperCase() === 'CLOUD';

  const [isEnabled] = useFeatureToggle();
  const isOmsServiceV2Enabled = isEnabled(ENABLE_OMS_SERVICE_V2);

  const { isApprovalDeskEnabled } = useApprovalDeskPermissions();

  const {
    cancelApprovalRequestError,
    cancelApprovalRequestProcessing,
    submitRequestAfterApprovalCancelation,
  } = useCancelRequest({
    businessKey: entitlement.entitlementNumber,
  });

  const { hasOpenApprovalRequest, error: taskListError, data: taskListData } = useTaskList({
    businessKey: entitlement.entitlementNumber,
    skip: !entitlement.entitlementNumber || !isApprovalDeskEnabled,
  });

  const header = () => {
    return (
      <div className="header-box">
        <EntitlementIcon
          logoId={entitlement?.logoId as string}
          productId={productId as string}
          className="avatar medium"
          isCloud={isCloud}
        />
        <h4 className="product-header">{`${productName} ${entitlementNumber}`}</h4>
      </div>
    );
  };

  const { error: formError, isProcessing, submitForm, complete } = useFormSubmit<any, any>({
    submitFormAction: isOmsServiceV2Enabled ? updateEntitlementPlanV2 : updateEntitlementPlan,
    onComplete: () => {
      closeSidebar();
      refetch();
    },
  });

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

  const getNotificationOption = (id: string, header: string) => {
    const notificationOptions = successNotificationBuilder();

    return notificationOptions
      ?.withId(id)
      .withHeader(header)
      .withMessage(getNotificationContent())
      .withDismissAfter(3000)
      .isVersion(NotificationTypes.FLAG)
      .build();
  };
  if (complete && !notificationSent) {
    const notificationOptions = getNotificationOption('rebill-subscription-success', 'Rebill done');
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);

    analytics.sendTrackEvent({
      action: AnalyticsTrackAction.REBILL,
      actionSubject: AnalyticsTrackSubject.SUBSCRIPTION,
      attributes: {
        entitlementId: entitlement.id,
      },
    });
  }

  const constructRequestData = (entitlementId: string, formData: FormData) => {
    const requestData: UpdateOrderRequest = {
      metadata: constructMetadata(formData),
      entitlementId: entitlementId,
      rebillOptions: {
        billFromTimeStamp: moment().valueOf(),
      },
      productId: entitlement.productId ?? '', //@ts-ignore
      chargeQuantities: entitlement?.scheduledChanges?.chargeQuantities || [],
      offeringId: entitlement.offeringId as string,
    };

    if (isOmsServiceV2Enabled) {
      const constructV2Request = {
        metadata: constructMetadata(formData),
        orderType: OrderItemType.REBILL_ORDER,
        rebillOptions: {
          billFromTimeStamp: moment().utc().valueOf(),
        },
        entitlements: [
          {
            entitlementId: entitlement.id,
            productId: entitlement?.productId as string,
          },
        ],
        transactionAccountId: entitlement?.transactionAccountId as string,
        invoiceGroupId: entitlement?.billingGroupId as string,
      };
      return constructCustomOrderRequest(constructV2Request);
    }
    return requestData;
  };

  const constructMetadata = (formData: FormData) => {
    const metadata: Record<string, string> = {};
    if (formData.handNote) {
      metadata[OMSMetadata.HAND_NOTE] = formData.handNote;
    }
    if (formData.supportTicketNumber) {
      metadata[OMSMetadata.SUPPORT_TICKET_NUMBER] = formData.supportTicketNumber;
    }
    return metadata;
  };

  const handleFormSubmit = (formData: FormData) => {
    const requestData = constructRequestData(entitlementId, formData);
    if (isApprovalDeskEnabled) {
      submitRequestAfterApprovalCancelation(() => submitForm(requestData), hasOpenApprovalRequest);
    } else {
      submitForm(requestData);
    }
  };

  return (
    <div className="rebill-subscription-container">
      <Form onSubmit={handleFormSubmit}>
        {({ formProps, dirty }) => (
          <form {...formProps}>
            <FormHeader title={'Rebill subscription'} description={header()}></FormHeader>
            {!!taskListError && (
              <FormSection>
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to request rebill"
                      message={taskListError.message}
                    />
                  }
                />
              </FormSection>
            )}
            {!!cancelApprovalRequestError && (
              <FormSection>
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to request rebill"
                      message={cancelApprovalRequestError.message}
                    />
                  }
                />
              </FormSection>
            )}
            {!!formError && (
              <InfoPanel
                type="error"
                message={
                  <ErrorComponents.ErrorMessageOnInfoPanel
                    title="We're unable to rebill for the following reason"
                    message={formError || 'Something went wrong.'}
                  />
                }
              />
            )}
            {hasOpenApprovalRequest && (
              <FormSection>
                <InfoPanel
                  type="warning"
                  message={
                    <div className="approval-request-inflight-banner">
                      One or more approval requests are currently in progress. If you perform this action, those
                      approval requests will be canceled automatically.
                      {taskListData?.taskList?.tasks?.length && (
                        <TaskDetailAction
                          task={taskListData.taskList.tasks[0]}
                          text="View approval request details"
                          refetch={refetch}
                          refetchAll={refetch}
                        />
                      )}
                    </div>
                  }
                />
              </FormSection>
            )}
            <div className="warning-container" data-testid="warning-container">
              <SectionMessage appearance="warning">
                <p>
                  The recharge to the customer will be immediate. <br /> No refunds will be generated as part of this
                  rebill. Refunds will first need to be requested from the invoice page.
                </p>
              </SectionMessage>
            </div>
            <SummarySectionV2.SummarySection columns="200px 200px 200px" className="rebill-subscription-summary">
              <SummarySectionV2.SummaryGridCalloutItem testId="summary-add-ons">
                <div className="billing-plan billing-cycle title-height" data-testid="last-bill-date">
                  <label>Last bill date</label>
                  <div>
                    <CopyableText>
                      <formattedV2.FormattedDate
                        dateFormat={NEW_DESIGN_DATE_FORMAT}
                        date={entitlement?.latestPeriodStartDate}
                      />
                    </CopyableText>
                  </div>
                </div>
              </SummarySectionV2.SummaryGridCalloutItem>
              <SummarySectionV2.SummaryGridCalloutItem testId="summary-add-ons">
                <div className="billing-plan billing-cycle title-height" data-testid="plan">
                  <label>Plan</label>
                  <div>{entitlement?.offering?.name}</div>
                </div>
              </SummarySectionV2.SummaryGridCalloutItem>
              <SummarySectionV2.SummaryGridCalloutItem testId="summary-add-ons">
                <div className="billing-plan billing-cycle title-height" data-testid="billing-cycle">
                  <label>Billing cycle</label>
                  <div>
                    <span>
                      {entitlement?.billingFrequency &&
                        entitlement?.billingFrequency !== 'OTHER' &&
                        `${capitalize(entitlement?.billingFrequency)} `}
                    </span>
                  </div>
                </div>
              </SummarySectionV2.SummaryGridCalloutItem>
              <SummarySectionV2.SummaryGridCalloutItem testId="summary-add-ons">
                <div className="billing-plan billing-cycle title-height" data-testid="previous-user-count">
                  <label>Previous user count</label>
                  <div>{entitlement?.totalBilledUsers ?? 'N/A'}</div>
                </div>
              </SummarySectionV2.SummaryGridCalloutItem>
              <SummarySectionV2.SummaryGridCalloutItem testId="summary-add-ons">
                <div className="billing-plan billing-cycle title-height" data-testid="new-user-count">
                  <label>User count changing to</label>
                  <div>{entitlement?.activeUsers}</div>
                </div>
              </SummarySectionV2.SummaryGridCalloutItem>
            </SummarySectionV2.SummarySection>
            <FormSection>
              <Field
                name="supportTicketNumber"
                label={'Support ticket number'}
                isRequired={true}
                validate={(value: any) => (value && value.length > 0 ? undefined : 'INVALID')}
              >
                {({ fieldProps, error }) => (
                  <>
                    <Textfield
                      {...fieldProps}
                      name="supportTicketNumber"
                      placeholder={'Add GSAC ticket number'}
                      testId="support-ticket-number"
                    />
                    {error && <ErrorMessage>Enter a valid ticket number</ErrorMessage>}
                  </>
                )}
              </Field>
            </FormSection>
            <FormSection>
              <Field
                label={'Internal comment'}
                name="handNote"
                isRequired={true}
                validate={(value: any) => (value && value.length > 0 ? undefined : 'INVALID')}
              >
                {({ fieldProps, error }) => (
                  <>
                    <Textfield {...fieldProps} name="handNote" testId="hand-note" placeholder={'Add comment'} />
                    {error && <ErrorMessage>This field is required</ErrorMessage>}
                  </>
                )}
              </Field>
            </FormSection>
            <FormFooter align="end">
              <span className="form-footer-btn-wrapper">
                <ButtonGroup>
                  <Button
                    appearance="subtle"
                    onClick={() => closeSidebar()}
                    testId="cancel"
                    className="close-sidebar-btn"
                  >
                    Close
                  </Button>
                  <LoadingButton
                    appearance={'warning'}
                    type="submit"
                    isLoading={isProcessing || cancelApprovalRequestProcessing}
                    testId={'rebill-subscription'}
                    aria-label="Rebill subscription"
                    isDisabled={!dirty || !!taskListError}
                  >
                    Rebill
                  </LoadingButton>
                </ButtonGroup>
              </span>
            </FormFooter>
          </form>
        )}
      </Form>
    </div>
  );
};

export default withApollo(RebillSubscription);
